import React, { useState, useRef, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const getToken = () => window.localStorage.getItem('authorization');

const downloadAttachment = async (attachmentUrl, originalName) => {
  try {
    const res = await fetch(`${API_URL}${attachmentUrl}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = originalName || 'attachment';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch (e) {
    // silent
  }
};

const AttachmentDisplay = ({ attachment }) => {
  const [imgSrc, setImgSrc] = useState(null);
  const blobUrlRef = useRef(null);
  const isImage = attachment?.contentType?.startsWith('image/');

  useEffect(() => {
    if (!isImage || !attachment?.url) return undefined;
    let cancelled = false;

    fetch(`${API_URL}${attachment.url}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(r => r.blob())
      .then(blob => {
        if (cancelled) return;
        const blobUrl = URL.createObjectURL(blob);
        blobUrlRef.current = blobUrl;
        setImgSrc(blobUrl);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachment && attachment.url, isImage]);

  if (!attachment?.url) return null;

  const handleClick = () => downloadAttachment(attachment.url, attachment.originalName);
  const sizeKb = attachment.size ? `${(attachment.size / 1024).toFixed(0)} KB` : '';

  if (isImage && imgSrc) {
    return (
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
      <img
        src={imgSrc}
        alt={attachment.originalName || 'Image attachment'}
        onClick={handleClick}
        style={{
          maxWidth: '220px',
          maxHeight: '180px',
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'block',
          marginTop: '6px',
        }}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginTop: '6px',
        padding: '6px 10px',
        borderRadius: '6px',
        border: 'none',
        background: 'rgba(0,0,0,0.06)',
        color: '#334155',
        cursor: 'pointer',
        fontSize: '12px',
        maxWidth: '100%',
        textAlign: 'left',
      }}
    >
      <i className="fas fa-paperclip" />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {attachment.originalName || 'Attachment'}
      </span>
      {sizeKb && (
        <span style={{ opacity: 0.6, flexShrink: 0 }}>{sizeKb}</span>
      )}
    </button>
  );
};

const getSenderTheme = ({ senderLabel, senderType }) => {
  const normalizedLabel = (senderLabel || '').toLowerCase();

  if (senderType === 'admin' && normalizedLabel.includes('kajetan borecki')) {
    return {
      bg: '#dbeafe',
      border: '#3b82f6',
      label: '#1d4ed8',
    };
  }

  if (
    senderType === 'admin'
    || normalizedLabel.includes('agencja')
    || normalizedLabel.includes('admin')
    || normalizedLabel.includes('playsportusa')
    || normalizedLabel.includes('asystent')
  ) {
    return {
      bg: '#fef3c7',
      border: '#f59e0b',
      label: '#a16207',
    };
  }

  if (senderType === 'coach' || normalizedLabel.includes('trener')) {
    return {
      bg: '#dcfce7',
      border: '#22c55e',
      label: '#15803d',
    };
  }

  return null;
};

const MessageBubble = ({ message, isSentByMe, senderLabel, bubbleColor, senderType }) => {
  const date = new Date(message.createdAt);
  const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const dateStr = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  const senderTheme = getSenderTheme({ senderLabel, senderType });

  const borderColor = senderTheme
    ? senderTheme.border
    : bubbleColor
      ? '#f59e0b'
      : isSentByMe
      ? '#2e496b'
      : '#e2e8f0';

  const bgColor = senderTheme
    ? senderTheme.bg
    : bubbleColor
      || (isSentByMe ? '#f0f4f8' : '#fafafa');

  return (
    <div
      style={{
        marginBottom: '2px',
        borderLeft: `3px solid ${borderColor}`,
        background: bgColor,
        padding: '12px 16px',
      }}
    >
      {/* Header row: sender left, date+receipts right */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '6px',
        }}
      >
        <span
          style={{
            fontSize: '13px',
            fontWeight: '600',
            color: senderTheme?.label || (isSentByMe ? '#2e496b' : '#334155'),
          }}
        >
          {senderLabel || (isSentByMe ? 'You' : 'Unknown')}
        </span>
        <span style={{ fontSize: '11px', color: '#94a3b8' }}>
          {dateStr} {timeStr}
          {isSentByMe && message.readAt && (
            <span style={{ marginLeft: '4px', color: '#2e496b' }}>&#10003;&#10003;</span>
          )}
        </span>
      </div>

      {/* Message body */}
      {message.text && (
        // eslint-disable-next-line react/no-danger
        <div
          className="msg-card-content"
          style={{ fontSize: '14px', lineHeight: '1.5', color: '#1e293b', wordBreak: 'break-word' }}
          dangerouslySetInnerHTML={{ __html: message.text }}
        />
      )}
      {message.attachment && (
        <AttachmentDisplay attachment={message.attachment} />
      )}
    </div>
  );
};

export default MessageBubble;
